import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { getTerminalSettings } from "@api";
import { ProfilePaymentIcon } from "@components";
import { SectionTitle, SectionTagline, SwitchInput } from "../components";
import usePaymentSettingsForm from "../hooks/usePaymentSettingsForm";

const PASSCODE_FIELDS = [
  { key: "adminMenuPin", label: "Admin menu" },
  { key: "refundPin", label: "Refunds" },
  { key: "txMenuPin", label: "Transactions menu" },
  { key: "screenLockPin", label: "Screen lock" },
];

const Passcode = ({ value, revealed }) => (
  <span className="font-mono text-sm text-zinc-900">
    {value ? (revealed ? value : "••••") : "Not set"}
  </span>
);

const TerminalPasscodesRow = ({ terminal }) => {
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="py-3 border-b border-zinc-100 last:border-b-0">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm font-medium text-zinc-900">
            {terminal.friendlyName}
          </span>
          <span className="ms-3 text-xs text-zinc-500">
            {[terminal.branch, terminal.providerTerminalId]
              .filter(Boolean)
              .join(" • ")}
          </span>
        </div>
        {terminal.passcodes && (
          <button
            type="button"
            onClick={() => setRevealed((r) => !r)}
            className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"
            title={revealed ? "Hide passcodes" : "Show passcodes"}
          >
            {revealed ? <EyeSlash /> : <Eye />}
          </button>
        )}
      </div>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
        {PASSCODE_FIELDS.map(({ key, label }) => (
          <div key={key} className="flex flex-col">
            <span className="text-xs text-zinc-500">{label}</span>
            <Passcode value={terminal.passcodes?.[key]} revealed={revealed} />
          </div>
        ))}
      </div>
    </div>
  );
};

const Terminals = () => {
  const {
    merchant,
    paymentRequests,
    checkout,
    corporateCardSurchargePercent,
    klarnaAccountEmail,
    passOnCorporateCardFee,
    checkoutPassOnCorporateCardFee,
    terminals: terminalSettings,
    submitPaymentSettings,
  } = usePaymentSettingsForm();

  const [terminals, setTerminals] = useState([]);
  const [terminalsLoading, setTerminalsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { control, setValue } = useForm({
    defaultValues: { allowAmex: terminalSettings?.allowAmex ?? false },
  });

  useEffect(() => {
    setValue("allowAmex", terminalSettings?.allowAmex ?? false);
  }, [terminalSettings, setValue]);

  useEffect(() => {
    const load = async () => {
      try {
        setTerminals(await getTerminalSettings());
      } finally {
        setTerminalsLoading(false);
      }
    };
    load();
  }, []);

  const onAmexChange = async (allowAmex) => {
    setSaving(true);
    try {
      // The full payment-settings payload is assembled here; only the terminal
      // AMEX flag changes. The reload snaps the switch back if the update failed.
      await submitPaymentSettings({
        paymentRequests,
        checkout,
        corporateCardSurchargePercent,
        klarnaAccountEmail,
        passOnCorporateCardFee,
        checkoutPassOnCorporateCardFee,
        terminals: { ...terminalSettings, allowAmex },
      });
    } finally {
      setSaving(false);
    }
  };

  if (!merchant) return null;

  return (
    <div className="flex flex-col items-center py-4 px-5 overflow-auto">
      <div className="py-8 w-full max-w-4xl flex flex-col gap-y-8">
        <div className="flex flex-col gap-y-1">
          <SectionTitle>Terminals</SectionTitle>
          <SectionTagline>
            Configure card schemes and view passcodes for your POS terminals
          </SectionTagline>
        </div>

        <div className="w-full flex flex-col items-start gap-y-2">
          <p className="font-semibold m-0">Payment methods</p>
          <div className="rounded-3xl bg-white shadow-[4px_4px_16px_0px_rgba(0,0,0,0.16)] py-4 px-6 w-full">
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-6">
                <ProfilePaymentIcon paymentType="Card" />
                <span className="text-sm font-medium text-zinc-900">
                  Visa / Mastercard
                </span>
              </div>
              <span className="text-sm text-zinc-500">Always on</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-6">
                <ProfilePaymentIcon paymentType="Amex" />
                <span className="text-sm font-medium text-zinc-900">
                  American Express
                </span>
              </div>
              <SwitchInput
                name="allowAmex"
                control={control}
                disabled={saving}
                onChange={onAmexChange}
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-6">
                <ProfilePaymentIcon paymentType="Card" />
                <span className="text-sm font-medium text-zinc-900">
                  Corporate card fee
                </span>
              </div>
              <span className="text-sm text-zinc-500">
                {terminalSettings?.passOnCorporateCardFee
                  ? `${corporateCardSurchargePercent || 2}%`
                  : "Off"}
                {" · managed in General settings"}
              </span>
            </div>
            <p className="text-xs text-zinc-500 pt-2 m-0">
              Applies to all of your terminals. Changes can take a few minutes
              to reach the terminals. Terminals must be online to accept the changes.
            </p>
          </div>
        </div>

        <div className="w-full flex flex-col items-start gap-y-2">
          <p className="font-semibold m-0">Terminal passcodes</p>
          <div className="rounded-3xl bg-white shadow-[4px_4px_16px_0px_rgba(0,0,0,0.16)] py-4 px-6 w-full">
            {terminalsLoading ? (
              <p className="text-sm text-zinc-400 py-2 m-0">Loading...</p>
            ) : terminals.length === 0 ? (
              <p className="text-sm text-zinc-500 py-2 m-0">
                No terminals have been registered yet.
              </p>
            ) : (
              terminals.map((terminal) => (
                <TerminalPasscodesRow key={terminal.id} terminal={terminal} />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terminals;
